import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiShield, FiPlus, FiEdit3, FiTrash2, FiX, FiSearch, FiCheck, FiDownload, FiKey, FiEye, FiEyeOff, FiAlertOctagon,
} from 'react-icons/fi';
import {
  listarCertificados, criarCertificado, atualizarCertificado, excluirCertificado,
  baixarCertificado, buscarSenhaCertificado, listarFaltandoCertificado, dispensarCertificado, revogarDispensaCertificado,
} from '../../../services/certificados';
import { formatarDocumento } from '../../../utils/documento';
import { faixaProcuracao } from '../../../services/procuracoes';
import { listarClientes } from '../../../services/clientes';
import {
  formatarData, textoPrazo, CardsResumo, clientesDoFiltro, itensDoAlerta, ModalAlerta,
} from '../procuracoes/comum';
/* mesma tela das procurações, mesmo visual -- o CSS é compartilhado */
import styles from '../procuracoes/Procuracoes.module.css';
import useEsc from '../../../hooks/useEsc';

const NOMES_SUGERIDOS = ['e-CNPJ A1', 'e-CPF A1', 'e-CNPJ A3', 'e-CPF A3', 'NF-e A1'];

const CHAVE_ALERTAS = 'certificados.alertasDispensados';

/* ─── Formulário ─── */
function ModalCertificado({ registro, clientes, onFechar, onSalvo }) {
  useEsc(onFechar);
  const editando = !!registro?.id;
  /* aberto pela aba "Falta certificado": cliente já escolhido */
  const [form, setForm] = useState({
    cliente_id: registro?.clienteId || registro?.novoParaCliente || '',
    nome:       registro?.nome || '',
    vencimento: registro?.vencimento || '',
    senha:      '',
    observacao: registro?.observacao || '',
    /* tipo conferido com o arquivo pelo servidor; novo já vem e-CNPJ */
    tipo:       registro?.tipo || 'e-cnpj',
  });
  const [arquivo, setArquivo] = useState(null);
  const [verSenha, setVerSenha] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })); }

  /* arquivo novo = certificado renovado: limpa o vencimento pra o backend
     ler a validade nova de dentro do .pfx, em vez de manter a antiga */
  function escolherArquivo(f) {
    setArquivo(f || null);
    if (f && editando) set('vencimento', '');
  }

  async function salvar() {
    setSalvando(true); setErro('');
    try {
      let r;
      if (editando) {
        const { nome, vencimento, senha, observacao, tipo } = form;
        r = await atualizarCertificado(registro.id, { nome, vencimento, senha, observacao, tipo }, arquivo);
      } else {
        r = await criarCertificado(form, arquivo);
      }
      onSalvo(r?.aviso || '');
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <h3 className={styles.modalTitulo}>{editando ? 'Editar certificado' : 'Novo certificado'}</h3>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>

        <div className={styles.modalCorpo}>
          <label className={styles.campoLabel}>Cliente</label>
          {editando ? (
            <div className={styles.valorFixo}>{registro.codigo} · {registro.clienteNome}</div>
          ) : (
            <select className={styles.campoInput} value={form.cliente_id}
              onChange={e => set('cliente_id', e.target.value)}>
              <option value="">Selecione...</option>
              {clientes.map(c => (
                <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
              ))}
            </select>
          )}

          <label className={styles.campoLabel}>Tipo</label>
          <select className={styles.campoInput} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
            <option value="e-cnpj">e-CNPJ (da empresa)</option>
            <option value="e-cpf">e-CPF (pessoa física)</option>
          </select>
          <p className={styles.dica}>O sistema confere com o documento que está dentro do arquivo.</p>

          <label className={styles.campoLabel}>Nome</label>
          <input className={styles.campoInput} list="nomes-certificado" value={form.nome}
            onChange={e => set('nome', e.target.value)} placeholder="e-CNPJ A1, e-CPF A1..." />
          <datalist id="nomes-certificado">
            {NOMES_SUGERIDOS.map(t => <option key={t} value={t} />)}
          </datalist>

          <label className={styles.campoLabel}>Arquivo do certificado</label>
          {editando && registro.arquivoNome && !arquivo && (
            <div className={styles.valorFixo}>{registro.arquivoNome}</div>
          )}
          <input className={styles.campoInput} type="file" accept=".pfx,.p12"
            onChange={e => escolherArquivo(e.target.files?.[0])} />
          {editando && <p className={styles.dica}>Escolha um arquivo só se for trocar o certificado (renovação).</p>}

          <label className={styles.campoLabel}>Senha</label>
          <div className={styles.senhaWrap}>
            <input className={styles.campoInput} type={verSenha ? 'text' : 'password'} value={form.senha}
              autoComplete="new-password"
              onChange={e => set('senha', e.target.value)}
              placeholder={editando && registro.temSenha ? 'Deixe em branco para manter a atual' : ''} />
            <button type="button" className={styles.btnIcone} title={verSenha ? 'Ocultar' : 'Mostrar'}
              onClick={() => setVerSenha(v => !v)}>
              {verSenha ? <FiEyeOff size={14} /> : <FiEye size={14} />}
            </button>
          </div>

          <label className={styles.campoLabel}>Vencimento</label>
          <input className={styles.campoInput} type="date" value={form.vencimento}
            onChange={e => set('vencimento', e.target.value)} />
          <p className={styles.dica}>Deixe em branco que o sistema lê a validade de dentro do arquivo (precisa da senha certa).</p>

          <label className={styles.campoLabel}>Observações</label>
          <textarea className={styles.campoTextarea} rows={2} value={form.observacao}
            onChange={e => set('observacao', e.target.value)} />

          {erro && <p className={styles.erro}>{erro}</p>}
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={onFechar}>Cancelar</button>
          <button type="button" className={styles.btnPrimario} onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Empresas sem e-CNPJ válido (card "Falta certificado") ───
   Cliente pessoa física não entra e e-CPF não conta. "Incluir
   certificado" abre o cadastro já com o cliente escolhido. */
function ModalDispensa({ empresa, onFechar, onSalvo }) {
  useEsc(onFechar);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  async function salvar() {
    setErro(''); setSalvando(true);
    try { await dispensarCertificado(empresa.clienteId, motivo); onSalvo(); }
    catch (e) { setErro(e.message); setSalvando(false); }
  }
  return (
    <div className={styles.modalFundo} style={{ zIndex: 1100 }}>
      <div className={styles.modalCaixa} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <h3 className={styles.modalTitulo}>Dispensar e-CNPJ · {empresa.codigo} - {empresa.nome}</h3>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={styles.modalCorpo}>
          <label className={styles.campoLabel}>Por que esta empresa não vai ter certificado? *</label>
          <textarea className={styles.campoTextarea} rows={4} autoFocus value={motivo} onChange={e => setMotivo(e.target.value)}
            placeholder="Ex.: empresa sem movimento, só declarações anuais feitas pelo escritório com procuração" />
          <p className={styles.dica}>A empresa sai da cobrança. Dá para revogar depois; a justificativa fica registrada com seu usuário.</p>
          {erro && <p className={styles.erro}>{erro}</p>}
        </div>
        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={onFechar}>Cancelar</button>
          <button type="button" className={styles.btnPrimario} onClick={salvar} disabled={salvando || motivo.trim().length < 5}>
            {salvando ? 'Salvando...' : 'Dispensar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalFaltando({ itens, dispensados = [], onFechar, onIncluir, onMudou }) {
  useEsc(onFechar);
  const [busca, setBusca] = useState('');
  const [dispensando, setDispensando] = useState(null);
  const [verDispensados, setVerDispensados] = useState(false);
  async function revogar(d) {
    if (!window.confirm(`Revogar a dispensa de ${d.nome}? A empresa volta a ser cobrada.`)) return;
    try { await revogarDispensaCertificado(d.id); onMudou(); } catch (e) { window.alert(e.message); }
  }
  const q = busca.trim().toLowerCase();
  const lista = itens.filter(f => !q || f.nome.toLowerCase().includes(q) || (f.codigo || '').includes(q));
  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <h3 className={styles.modalTitulo}>Falta certificado · {itens.length} empresa(s) sem e-CNPJ válido</h3>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={styles.modalCorpo}>
          <input className={styles.campoInput} placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
          <p className={styles.dica}>Clientes pessoa física (CPF) não entram. e-CPF não conta como certificado da empresa.</p>
          {lista.length === 0 ? <p className={styles.vazio}>Nenhuma empresa nessa condição.</p> : lista.map(f => (
            <div key={f.clienteId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 4px', borderBottom: '1px solid #eef3f8' }}>
              <strong style={{ color: '#6B8CAE', minWidth: 42 }}>{f.codigo}</strong>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 13 }}>{f.nome}</span>
                <span style={{ fontSize: 12, color: '#6B8CAE' }}>{formatarDocumento(f.cnpj)} · </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>
                  {f.situacao === 'vencido' ? `e-CNPJ vencido em ${formatarData(f.vencimento)}` : 'sem e-CNPJ vinculado'}
                </span>
              </span>
              <button type="button" className={styles.btnSecundario} onClick={() => setDispensando(f)}
                title="Esta empresa não vai ter certificado (pede justificativa)">
                Dispensar
              </button>
              <button type="button" className={styles.btnPrimario} onClick={() => onIncluir(f)}>
                <FiPlus size={13} /> Incluir certificado
              </button>
            </div>
          ))}
          {dispensados.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button type="button" className={styles.btnSecundario} onClick={() => setVerDispensados(v => !v)}>
                {verDispensados ? 'Ocultar' : 'Ver'} dispensadas ({dispensados.length})
              </button>
              {verDispensados && dispensados.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #eef3f8' }}>
                  <strong style={{ color: '#6B8CAE', minWidth: 42 }}>{d.codigo}</strong>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: 13 }}>{d.nome}</span>
                    <span style={{ fontSize: 12, color: '#4A6B8A' }}>{d.motivo}</span>
                    <span style={{ display: 'block', fontSize: 11, color: '#93a1b3' }}>dispensada por {d.por || '—'} em {d.em}</span>
                  </span>
                  <button type="button" className={styles.btnSecundario} onClick={() => revogar(d)}>Revogar</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {dispensando && (
        <ModalDispensa empresa={dispensando} onFechar={() => setDispensando(null)}
          onSalvo={() => { setDispensando(null); onMudou(); }} />
      )}
    </div>
  );
}

/* ─── Tela ─── */
function Certificados() {
  /* clientes "Pendente" (vindos do onboarding): fixados no topo */
  const [pendentes, setPendentes] = useState([]);
  const [itens, setItens]       = useState([]);
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]         = useState('');
  const [aviso, setAviso]       = useState('');
  const [busca, setBusca]       = useState('');
  const [filtro, setFiltro]     = useState(null); // card de resumo clicado
  const [editando, setEditando] = useState(null);
  const [alertaFechado, setAlertaFechado] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [senhasVisiveis, setSenhasVisiveis] = useState({}); // id -> senha aberta no quadro
  /* aba "Falta certificado": empresas sem e-CNPJ válido */
  const [faltandoAberto, setFaltandoAberto] = useState(false);
  const [faltando, setFaltando] = useState([]);
  const [dispensados, setDispensados] = useState([]);
  const carregarFaltando = useCallback(() => listarFaltandoCertificado().then(r => { setFaltando(r.items || []); setDispensados(r.dispensados || []); }).catch(() => {}), []);
  useEffect(() => { carregarFaltando(); }, [carregarFaltando]);

  const carregar = useCallback(() => {
    setCarregando(true);
    return listarCertificados()
      .then(r => { setItens((r.items || []).map(c => ({ ...c, descricao: c.nome }))); setPendentes(r.pendentes || []); })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { listarClientes().then(setClientes).catch(() => {}); }, []);

  /* backend manda plano; a tela é uma linha por cliente (ordem do backend
     já vem por código do cliente) */
  const grupos = useMemo(() => {
    const porCliente = new Map();
    itens.forEach(c => {
      if (!porCliente.has(c.clienteId)) {
        porCliente.set(c.clienteId, { clienteId: c.clienteId, codigo: c.codigo, nome: c.clienteNome, certificados: [] });
      }
      porCliente.get(c.clienteId).certificados.push(c);
    });
    /* pendente fica no topo, inclusive quem ainda não tem certificado */
    const ids = new Set(pendentes.map(p => p.clienteId));
    pendentes.forEach(p => {
      if (!porCliente.has(p.clienteId)) porCliente.set(p.clienteId, { clienteId: p.clienteId, codigo: p.codigo, nome: p.clienteNome, certificados: [] });
    });
    return [...porCliente.values()].map(g => ({ ...g, pendente: ids.has(g.clienteId) })).sort((a, b) => b.pendente - a.pendente);
  }, [itens, pendentes]);

  const alertas = useMemo(() => itensDoAlerta(itens, CHAVE_ALERTAS), [itens]);

  const q = busca.trim().toLowerCase();
  const doFiltro = clientesDoFiltro(itens, filtro);
  const visiveis = grupos.filter(g => {
    if (doFiltro && !doFiltro.has(g.clienteId)) return false;
    if (!q) return true;
    return g.nome.toLowerCase().includes(q) || (g.codigo || '').includes(q)
      || g.certificados.some(c => c.nome.toLowerCase().includes(q));
  });

  async function excluir(c) {
    if (!window.confirm(`Excluir o certificado "${c.nome}" de ${c.clienteNome}? O arquivo também será apagado.`)) return;
    try {
      await excluirCertificado(c.id);
      carregar();
    } catch (e) { setErro(e.message); }
  }

  async function baixar(c) {
    setErro('');
    try { await baixarCertificado(c.id, c.arquivoNome); }
    catch (e) { setErro(e.message); }
  }

  async function copiarSenha(c) {
    setErro('');
    let senha;
    try { senha = await buscarSenhaCertificado(c.id); }
    catch (e) { setErro(e.message); return; }
    try {
      await navigator.clipboard.writeText(senha);
      setAviso(`Senha do certificado "${c.nome}" de ${c.clienteNome} copiada.`);
      setTimeout(() => setAviso(''), 4000);
    } catch {
      /* navegador recusou a área de transferência: mostra pra copiar à mão */
      window.prompt('Senha do certificado:', senha);
    }
  }

  /* olho no quadro: busca a senha só quando alguém clica (cada pedido
     passa pela auditoria) e mostra ali mesmo; clicar de novo esconde */
  async function alternarSenha(c) {
    if (senhasVisiveis[c.id] !== undefined) {
      setSenhasVisiveis(({ [c.id]: _, ...resto }) => resto);
      return;
    }
    setErro('');
    try {
      const senha = await buscarSenhaCertificado(c.id);
      setSenhasVisiveis(s => ({ ...s, [c.id]: senha }));
    } catch (e) { setErro(e.message); }
  }

  const totalCertificados = visiveis.reduce((n, g) => n + g.certificados.length, 0);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcone}><FiShield size={20} /></div>
        <div className={styles.headerTexto}>
          <h1 className={styles.titulo}>Certificados</h1>
        </div>
        <div className={styles.buscaWrap}>
          <FiSearch size={14} className={styles.buscaIcone} />
          <input className={styles.campoInput} type="search" placeholder="Buscar cliente ou certificado..."
            value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <button type="button" className={styles.btnPrimario} onClick={() => setEditando({})}>
          <FiPlus size={14} /> Novo certificado
        </button>
        <button type="button"
          className={modoEdicao ? styles.btnEditarAtivo : styles.btnEditar}
          onClick={() => setModoEdicao(v => !v)}>
          {modoEdicao ? <><FiCheck size={13} /> Concluir</> : <><FiEdit3 size={13} /> Editar</>}
        </button>
      </div>

      <CardsResumo itens={itens} totalClientes={grupos.length} filtro={filtro} onFiltro={setFiltro}
        extras={[{ chave: 'faltando', icone: FiAlertOctagon, vermelho: true, valor: faltando.length,
          titulo: 'Falta certificado', sub: 'Empresas sem e-CNPJ', onClick: () => setFaltandoAberto(true) }]} />

      <div className={styles.conteudo}>
        <div className={styles.barraContador}>
          <span className={styles.contador}>
            {visiveis.length} {visiveis.length === 1 ? 'cliente' : 'clientes'} · {totalCertificados} {totalCertificados === 1 ? 'certificado' : 'certificados'}
          </span>
        </div>

        {erro && <p className={styles.erro}>{erro}</p>}
        {aviso && <p className={styles.aviso}>{aviso}</p>}

        {carregando ? (
          <p className={styles.vazio}>Carregando...</p>
        ) : visiveis.length === 0 ? (
          <p className={styles.vazio}>
            {grupos.length === 0
              ? 'Nenhum certificado cadastrado ainda.'
              : 'Nenhum cliente nessa condição.'}
          </p>
        ) : (
          <div className={styles.lista}>
            {visiveis.map(g => (
              <div key={g.clienteId} className={styles.linhaCliente}>
                <div className={styles.colCliente}>
                  <span className={styles.codigo}>{g.codigo}</span>
                  <span className={styles.nomeCliente}>{g.nome}</span>
                  {g.pendente && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: '#92400e', background: '#fef3c7', borderRadius: 5, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '0.03em' }} title="Cliente vindo do onboarding: cadastre o certificado">Pendente</span>}
                </div>
                <div className={styles.cards}>
                  {g.certificados.map(c => (
                    <div key={c.id} className={`${styles.card} ${styles[faixaProcuracao(c.diasRestantes)]}`}
                      title={c.observacao || undefined}>
                      <div className={styles.cardTexto}>
                        <span className={styles.cardTipo}>{c.nome}</span>
                        <span className={styles.cardPrazo}>
                          {c.vencimento ? `${formatarData(c.vencimento)} · ${textoPrazo(c.diasRestantes, true)}` : 'sem vencimento'}
                        </span>
                        {senhasVisiveis[c.id] !== undefined && (
                          <span className={styles.cardSenha} title="Senha do certificado">
                            {senhasVisiveis[c.id] || '(vazia)'}
                          </span>
                        )}
                      </div>
                      {/* baixar, ver e copiar senha ficam sempre à mão -- é o uso do dia a dia */}
                      <div className={styles.cardAcoes}>
                        {c.temArquivo && (
                          <button type="button" className={styles.btnIcone} title={`Baixar ${c.arquivoNome || 'certificado'}`}
                            onClick={() => baixar(c)}>
                            <FiDownload size={12} />
                          </button>
                        )}
                        {c.temSenha && (
                          <button type="button" className={styles.btnIcone}
                            title={senhasVisiveis[c.id] !== undefined ? 'Ocultar senha' : 'Ver senha'}
                            onClick={() => alternarSenha(c)}>
                            {senhasVisiveis[c.id] !== undefined ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                          </button>
                        )}
                        {c.temSenha && (
                          <button type="button" className={styles.btnIcone} title="Copiar senha"
                            onClick={() => copiarSenha(c)}>
                            <FiKey size={12} />
                          </button>
                        )}
                        {modoEdicao && (
                          <>
                            <button type="button" className={styles.btnIcone} title="Editar"
                              onClick={() => setEditando(c)}>
                              <FiEdit3 size={12} />
                            </button>
                            <button type="button" className={styles.btnIcone} title="Excluir"
                              onClick={() => excluir(c)}>
                              <FiTrash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {faltandoAberto && (
        <ModalFaltando itens={faltando} dispensados={dispensados} onMudou={carregarFaltando} onFechar={() => setFaltandoAberto(false)}
          onIncluir={(f) => setEditando({ novoParaCliente: f.clienteId })} />
      )}

      {editando && (
        <ModalCertificado
          registro={editando}
          clientes={clientes}
          onFechar={() => setEditando(null)}
          onSalvo={(avisoSalvo) => {
            setEditando(null);
            if (avisoSalvo) setAviso(avisoSalvo);
            setSenhasVisiveis({}); // a senha pode ter mudado na edição

            carregar();
            carregarFaltando();
          }}
        />
      )}

      {!alertaFechado && alertas.length > 0 && (
        <ModalAlerta itens={alertas} titulo="Certificados vencendo" singular="certificado"
          plural="certificados" masculino chave={CHAVE_ALERTAS} onFechar={() => setAlertaFechado(true)} />
      )}
    </div>
  );
}

export default Certificados;
